import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const T = {
  en: {
    loading: 'Loading…',
    invalidToken: 'This link is invalid or has already been used.',
    contact: 'If you think this is an error, email team@mstsportstours.com.',
    yesTitle: 'Great — you\'re confirmed!',
    yesBody: 'We\'ve recorded that your team can fulfil this fixture. MST Sports Tours will be in touch to confirm the details.',
    noTitle: 'Response recorded',
    noBody: 'We\'ve noted that your team is unable to fulfil this fixture. Thank you for letting us know.',
    suggestTitle: 'Suggest an alternative date & time',
    suggestBody: 'Please enter your preferred date and time below.',
    date: 'Preferred date',
    time: 'Preferred time',
    submit: 'Send suggestion',
    submitting: 'Sending…',
    suggestSentTitle: 'Suggestion received!',
    suggestSentBody: 'Thank you — we\'ve sent your suggested date and time to MST Sports Tours. We\'ll be in touch to confirm.',
    error: 'Something went wrong. Please try again or email team@mstsportstours.com.',
  },
  es: {
    loading: 'Cargando…',
    invalidToken: 'Este enlace no es válido o ya ha sido utilizado.',
    contact: 'Si crees que es un error, escribe a team@mstsportstours.com.',
    yesTitle: '¡Confirmado!',
    yesBody: 'Hemos registrado que tu equipo puede disputar este partido. MST Sports Tours se pondrá en contacto contigo para confirmar los detalles.',
    noTitle: 'Respuesta registrada',
    noBody: 'Hemos anotado que tu equipo no puede disputar este partido. Gracias por avisarnos.',
    suggestTitle: 'Sugerir una fecha y hora alternativa',
    suggestBody: 'Por favor, introduce tu fecha y hora preferidas a continuación.',
    date: 'Fecha preferida',
    time: 'Hora preferida',
    submit: 'Enviar sugerencia',
    submitting: 'Enviando…',
    suggestSentTitle: '¡Sugerencia recibida!',
    suggestSentBody: 'Gracias — hemos enviado tu sugerencia de fecha y hora a MST Sports Tours. Nos pondremos en contacto contigo para confirmarlo.',
    error: 'Algo salió mal. Inténtalo de nuevo o escribe a team@mstsportstours.com.',
  },
};

function Card({ children }) {
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
        {children}
      </div>
    </div>
  );
}

function CheckIcon({ colour }) {
  return (
    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${colour}`}>
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

export default function TeamDatabaseRespond() {
  const [searchParams] = useSearchParams();
  const token  = searchParams.get('token');
  const action = searchParams.get('action'); // 'yes' | 'no' | 'suggest'

  // Detect language from fixture record (fall back to browser)
  const [lang, setLang] = useState('en');
  const t = T[lang];

  const [phase, setPhase]       = useState('loading'); // loading | invalid | done-yes | done-no | suggest-form | done-suggest | error
  const [tokenData, setTokenData] = useState(null);
  const [suggestDate, setSuggestDate] = useState('');
  const [suggestTime, setSuggestTime] = useState('');
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token) { setPhase('invalid'); return; }

    async function init() {
      try {
        const tokenSnap = await getDoc(doc(db, 'fixtureTokens', token));
        if (!tokenSnap.exists()) { setPhase('invalid'); return; }

        const td = tokenSnap.data();
        setTokenData(td);

        // Check if already responded
        const responseSnap = await getDoc(
          doc(db, 'fixtureRequests', td.fixtureId, 'responses', td.teamId)
        );
        if (responseSnap.exists() && responseSnap.data().status !== 'pending') {
          // Already responded — show the existing status
          const status = responseSnap.data().status;
          setPhase(status === 'yes' ? 'done-yes' : status === 'no' ? 'done-no' : 'done-suggest');
          return;
        }

        // Try to read language from fixture
        const fixtureSnap = await getDoc(doc(db, 'fixtureRequests', td.fixtureId));
        if (fixtureSnap.exists() && fixtureSnap.data().language === 'es') setLang('es');

        if (action === 'yes' || action === 'no') {
          await respond(td, action);
        } else if (action === 'suggest') {
          setPhase('suggest-form');
        } else {
          setPhase('invalid');
        }
      } catch (err) {
        console.error(err);
        setPhase('error');
      }
    }

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function respond(td, act, extra = {}) {
    const responseRef = doc(db, 'fixtureRequests', td.fixtureId, 'responses', td.teamId);
    await updateDoc(responseRef, {
      status: act,
      respondedAt: serverTimestamp(),
      ...extra,
    });
    setPhase(act === 'yes' ? 'done-yes' : act === 'no' ? 'done-no' : 'done-suggest');
  }

  async function handleSuggest(e) {
    e.preventDefault();
    setFormError('');
    if (!suggestDate) { setFormError(lang === 'es' ? 'Por favor, introduce una fecha.' : 'Please enter a date.'); return; }
    if (!suggestTime) { setFormError(lang === 'es' ? 'Por favor, introduce una hora.' : 'Please enter a time.'); return; }
    setSaving(true);
    try {
      await respond(tokenData, 'suggest', { suggestedDate: suggestDate, suggestedTime: suggestTime });
    } catch (err) {
      console.error(err);
      setFormError(t.error);
    } finally {
      setSaving(false);
    }
  }

  if (phase === 'loading') {
    return (
      <Card>
        <p className="text-[#9CA3AF] text-[14px]">{t.loading}</p>
      </Card>
    );
  }

  if (phase === 'invalid') {
    return (
      <Card>
        <div className="w-12 h-12 bg-[#FEF2F2] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-[#991B1B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <p className="text-[15px] font-semibold text-[#111827] mb-2">{t.invalidToken}</p>
        <p className="text-[13px] text-[#6B7280]">{t.contact}</p>
      </Card>
    );
  }

  if (phase === 'error') {
    return (
      <Card>
        <p className="text-[15px] font-semibold text-[#111827] mb-2">Error</p>
        <p className="text-[13px] text-[#6B7280]">{t.error}</p>
      </Card>
    );
  }

  if (phase === 'done-yes') {
    return (
      <Card>
        <CheckIcon colour="bg-[#ECFDF5] text-[#1A7A4A]" />
        <p className="text-[17px] font-bold text-[#111827] mb-2">{t.yesTitle}</p>
        <p className="text-[13px] text-[#6B7280]">{t.yesBody}</p>
        <img
          src="/MST Logo White.png" alt="MST Sports Tours"
          className="h-8 mx-auto mt-6 opacity-30"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </Card>
    );
  }

  if (phase === 'done-no') {
    return (
      <Card>
        <div className="w-12 h-12 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-[#6B7280]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-[17px] font-bold text-[#111827] mb-2">{t.noTitle}</p>
        <p className="text-[13px] text-[#6B7280]">{t.noBody}</p>
      </Card>
    );
  }

  if (phase === 'done-suggest') {
    return (
      <Card>
        <CheckIcon colour="bg-[#EFF6FF] text-[#1A4A8A]" />
        <p className="text-[17px] font-bold text-[#111827] mb-2">{t.suggestSentTitle}</p>
        <p className="text-[13px] text-[#6B7280]">{t.suggestSentBody}</p>
      </Card>
    );
  }

  // suggest-form
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full">
        <div className="bg-[#0D2240] rounded-[10px] p-4 mb-6 text-center">
          <p className="text-white font-bold text-[15px]">MST Sports Tours</p>
        </div>
        <h2 className="text-[16px] font-bold text-[#111827] mb-1">{t.suggestTitle}</h2>
        <p className="text-[13px] text-[#6B7280] mb-5">{t.suggestBody}</p>
        <form onSubmit={handleSuggest} className="space-y-4">
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">{t.date}<span className="text-red-500 ml-0.5">*</span></label>
            <input
              type="date" value={suggestDate} onChange={(e) => setSuggestDate(e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">{t.time}<span className="text-red-500 ml-0.5">*</span></label>
            <input
              type="time" value={suggestTime} onChange={(e) => setSuggestTime(e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
            />
          </div>
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-[8px] p-3 text-[13px] text-red-700">{formError}</div>
          )}
          <button
            type="submit" disabled={saving}
            className="w-full py-3 bg-[#1A4A8A] text-white font-bold text-[14px] rounded-[10px] hover:bg-[#0D2240] transition disabled:opacity-60"
          >
            {saving ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
